/**
 * Player Narrative Tracking Service
 * 
 * Manages which terrain narratives each player has seen at specific hexes.
 * Ensures consistent revisit experience (same narrative when returning to a hex).
 */

import { getFirestore, doc, getDoc, updateDoc, arrayUnion } from 'firebase/firestore';

const db = getFirestore();

export interface SeenNarrative {
  hexKey: string; // Format: "q,r"
  contentId: string; // ID of storyContent document
}

/**
 * Check if player has seen a specific hex before
 * Returns the contentId if they have, null if first visit
 */
export async function getPlayerNarrativeForHex(
  userId: string,
  hexKey: string
): Promise<string | null> {
  try {
    const playerRef = doc(db, 'players', userId);
    const playerDoc = await getDoc(playerRef);
    
    if (!playerDoc.exists()) {
      console.warn(`Player ${userId} not found in Firestore`);
      return null;
    }
    
    const playerData = playerDoc.data();
    const seenNarratives: SeenNarrative[] = playerData.seenNarratives || [];
    
    // Find narrative for this hex
    const narrative = seenNarratives.find(n => n.hexKey === hexKey);
    
    if (narrative) {
      console.log(`✅ Player has seen hex ${hexKey} before: contentId ${narrative.contentId}`);
      return narrative.contentId;
    }
    
    console.log(`🆕 First visit to hex ${hexKey} for player ${userId}`);
    return null;
  } catch (error) {
    console.error(`Error checking player narrative for hex ${hexKey}:`, error);
    return null;
  }
}

/**
 * Record which narrative a player saw at a specific hex
 * This creates the memory for consistent revisits
 */
export async function recordPlayerNarrative(
  userId: string,
  hexKey: string,
  contentId: string
): Promise<void> {
  try {
    const playerRef = doc(db, 'players', userId);
    
    // Check if this hex already has a narrative recorded
    const existingContentId = await getPlayerNarrativeForHex(userId, hexKey);
    
    if (existingContentId) {
      console.log(`Hex ${hexKey} already recorded for player ${userId}, skipping`);
      return;
    }
    
    // Add to seenNarratives array
    await updateDoc(playerRef, {
      seenNarratives: arrayUnion({
        hexKey,
        contentId
      })
    });
    
    console.log(`📝 Recorded narrative for player ${userId}: hex ${hexKey} → content ${contentId}`);
  } catch (error) {
    console.error(`Error recording player narrative:`, error);
    throw error;
  }
}

/**
 * Get all contentIds the player has already seen (for filtering)
 * Used to ensure variety when selecting terrain narratives
 */
export async function getPlayerSeenContentIds(userId: string): Promise<string[]> {
  try {
    const playerRef = doc(db, 'players', userId);
    const playerDoc = await getDoc(playerRef);
    
    if (!playerDoc.exists()) {
      return [];
    }
    
    const playerData = playerDoc.data();
    const seenNarratives: SeenNarrative[] = playerData.seenNarratives || [];
    
    // Extract just the contentIds
    return seenNarratives.map(n => n.contentId);
  } catch (error) {
    console.error('Error getting player seen contentIds:', error);
    return [];
  }
}

/**
 * Get count of unique narratives player has seen
 */
export async function getPlayerNarrativeCount(userId: string): Promise<number> {
  try {
    const playerRef = doc(db, 'players', userId);
    const playerDoc = await getDoc(playerRef);
    
    if (!playerDoc.exists()) {
      return 0;
    }
    
    const playerData = playerDoc.data();
    const seenNarratives: SeenNarrative[] = playerData.seenNarratives || [];
    
    // Count unique contentIds (player might visit same narrative at different hexes)
    const uniqueContentIds = new Set(seenNarratives.map(n => n.contentId));
    return uniqueContentIds.size;
  } catch (error) {
    console.error('Error getting player narrative count:', error);
    return 0;
  }
}

/**
 * Clear seen narratives for specific contentIds (used when terrain cap is reached)
 * Removes these contentIds from player's seen list so they can see them again
 */
export async function clearSeenNarratives(
  userId: string,
  contentIds: string[]
): Promise<void> {
  try {
    const playerRef = doc(db, 'players', userId);
    const playerDoc = await getDoc(playerRef);
    
    if (!playerDoc.exists()) {
      console.warn(`Player ${userId} not found, nothing to clear`);
      return;
    }
    
    const playerData = playerDoc.data();
    const seenNarratives: SeenNarrative[] = playerData.seenNarratives || [];
    
    // Filter out the contentIds we want to clear
    const contentIdsSet = new Set(contentIds);
    const filteredNarratives = seenNarratives.filter(
      n => !contentIdsSet.has(n.contentId)
    );
    
    // Update player document
    await updateDoc(playerRef, {
      seenNarratives: filteredNarratives
    });
    
    const removedCount = seenNarratives.length - filteredNarratives.length;
    console.log(`🔄 Reset ${removedCount} seen narratives for player ${userId}`);
  } catch (error) {
    console.error('Error clearing seen narratives:', error);
    throw error;
  }
}

